import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdown(text) {
  if (!text) return "";
  let html = text
    .replace(/^### (.+)$/gm, '<h3 class="font-serif text-lg font-semibold text-[#1C2E4A] mt-6 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="font-serif text-xl font-semibold text-[#1C2E4A] mt-8 mb-3">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="font-serif text-2xl font-bold text-[#1C2E4A] mt-8 mb-3">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-[#1C2E4A]">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-[#C8A97E] pl-4 italic text-[#595959] my-4">$1</blockquote>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-[#595959]">$1</li>')
    .replace(/\n\n/g, '</p><p class="text-[#595959] leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>');
  return `<p class="text-[#595959] leading-relaxed mb-4">${html}</p>`;
}

export default function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${postId}`)
      .then(res => setPost(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-[#595959]">Loading...</div>;
  if (!post) return (
    <div className="min-h-[50vh] flex flex-col items-center justify-center text-[#595959]">
      <p className="text-lg mb-4">Article not found.</p>
      <Link to="/blog" className="text-[#C8A97E] hover:underline">Back to Blog</Link>
    </div>
  );

  return (
    <div data-testid="blog-post-page">
      <section className="bg-[#1C2E4A] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <h1 data-testid="blog-post-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 mt-4 text-sm text-white/50">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
        </div>
      </section>

      {post.featured_image && (
        <div className="max-w-4xl mx-auto px-6 md:px-12 -mt-8">
          <img src={post.featured_image} alt={post.title} className="w-full h-64 md:h-80 object-cover rounded-xl shadow-lg" />
        </div>
      )}

      <section className="py-12 md:py-20 bg-white">
        <article
          data-testid="blog-post-content"
          className="max-w-3xl mx-auto px-6 md:px-12 prose-sm"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
        />
      </section>

      <section className="py-12 bg-[#FAF9F6] border-t border-[#E5E2DC]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm text-[#595959] mb-4">Want to learn more about starting a church school?</p>
          <Link to="/start-a-church-school" className="inline-flex items-center gap-2 bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-2.5 rounded-md transition-colors font-medium text-sm">
            Start a Church School
          </Link>
        </div>
      </section>
    </div>
  );
}
